import React, { useState, useEffect } from 'react';
import { Button, makeStyles, Text, tokens, Spinner } from '@fluentui/react-components';
import { ArrowLeftRegular, HistoryRegular, CheckmarkCircle24Regular, PromptFilled } from '@fluentui/react-icons';
import { useTabStore } from '../../providers/TabProvider';
// import { useResultStore } from '../../providers/ResultProvider'; // Removed unused import
import { parseAIResult } from '../../utils/parseAIResult';
import InsightResultsContent from '../ai-insight-result/components/InsightResultsContent';
import DeepScanResultContent from '../deep-scan-result/components/DeepScanResultContent';
import { getScanResultByGroupId, getInsightByFingerprint } from '../../api';
import { InsightDataType, ScanResultByGroupIdType, IndicatorStatusType } from '../../types';

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor: tokens.colorNeutralBackground1,
    },
    header: {
        display: "flex",
        alignItems: "center",
        padding: "5px",
        gap: "8px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    backButton: {
        minWidth: "auto",
        padding: "4px",
    },
    tabSwitcher: {
        display: "flex",
        gap: "4px",
    },
    tabButton: {
        cursor: "pointer",
        padding: "8px 0",
        borderBottom: "2px solid transparent",
        fontWeight: 400,
        borderRadius: "0px",
        "&:hover": {
            borderBottom: `2px solid ${tokens.colorBrandForeground1}`,
            color: tokens.colorBrandForeground1,
        },
        "&.active": {
            borderBottom: `2px solid ${tokens.colorBrandForeground1}`,
            fontWeight: 600,
            color: tokens.colorBrandForeground1,
        },
    },
    banner: {
        margin: "16px",
        padding: "12px",
        backgroundColor: tokens.colorNeutralBackground2, // Light gray/blue tint
        borderRadius: "4px",
    },
    bannerHeader: {
        display: "flex",
        alignItems: "center",
        gap: "2px",
        color: tokens.colorBrandForeground1,
        fontWeight: 600,
        marginBottom: "4px",
    },
    bannerContent: {
        fontSize: "12px",
        color: tokens.colorNeutralForeground2,
    },
    contentArea: {
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    icon: {
        color: "#FF4AC9",
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
    }
});

type SubTab = "ai-insight-result" | "deep-scan-result";

export default function HistoryView() {
    const styles = useStyles();
    const { setActiveTab, selectedHistoryItem } = useTabStore();

    // Local state for fetched history data
    const [historyDeepScanData, setHistoryDeepScanData] = useState<{
        threatItems?: ScanResultByGroupIdType;
        indicatorStatus?: IndicatorStatusType;
    } | undefined>(undefined);
    const emailData = useTabStore((s) => s.emailData);
    const [historyInsightData, setHistoryInsightData] = useState<Partial<InsightDataType> | null>(null);
    const [loading, setLoading] = useState(false);

    // Default to deep-scan-result since that's the only data source from getScanResultByGroupId
    const [subTab, setSubTab] = useState<SubTab>("deep-scan-result");

    useEffect(() => {
        const fetchHistoryDetails = async () => {
            if (selectedHistoryItem) {
                setLoading(true);
                setHistoryDeepScanData(undefined);
                setHistoryInsightData(null);

                const [scanResult, insightResult] = await Promise.all([
                    getScanResultByGroupId({
                        groupId: selectedHistoryItem.group_id,
                        emailData: { emailFingerprint: selectedHistoryItem.email_fingerprint }
                    }),
                    getInsightByFingerprint({
                        emailFingerprint: selectedHistoryItem.email_fingerprint,
                        emailData: emailData,
                    }),
                ]);

                if (scanResult) {
                    setHistoryDeepScanData({
                        threatItems: scanResult.result,
                        indicatorStatus: {
                            "deep-scan": {
                                status: scanResult.overall_status,
                                description: scanResult.description,
                            }
                        }
                    });
                }

                setHistoryInsightData(insightResult);
                setLoading(false);
            }
        };

        fetchHistoryDetails();
    }, [selectedHistoryItem]);

    const handleBack = () => {
        setActiveTab("overview");
    };

    if (!selectedHistoryItem) {
        return <div>No item selected</div>;
    }

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <Button
                    appearance="transparent"
                    icon={<ArrowLeftRegular />}
                    onClick={handleBack}
                    className={styles.backButton}
                />
                {/* Custom Tab Switcher for History View */}
                <div className={styles.tabSwitcher}>
                    <Button
                        appearance="transparent"
                        icon={<PromptFilled className={useStyles().icon} />}
                        className={`${subTab === "ai-insight-result" ? "active" : ""} ${styles.tabButton} `}
                        onClick={() => setSubTab("ai-insight-result")}
                    >
                        AI Insight Result
                    </Button>
                    <Button
                        appearance="transparent"
                        className={`${subTab === "deep-scan-result" ? "active" : ""} ${styles.tabButton} `}
                        onClick={() => setSubTab("deep-scan-result")}
                    >
                        Deep Scan Result
                    </Button>
                </div>
            </div>

            {/* Previous Scan Banner */}
            <div className={styles.banner}>
                <div className={styles.bannerHeader}>
                    <HistoryRegular fontSize={20} />
                    <Text size={200} style={{ fontWeight: 600 }}>Previous Scan Result</Text>
                </div>
                <Text block style={{ fontWeight: 500, color: tokens.colorNeutralForeground1 }}>{selectedHistoryItem.subject}</Text>
                <Text block style={{ color: tokens.colorNeutralForeground2 }}>{selectedHistoryItem.sender}</Text>
            </div>

            {/* Content Area */}
            <div className={styles.contentArea}>
                {loading ? (
                    <div className={styles.loadingContainer}>
                        <Spinner label="Loading scan details..." />
                    </div>
                ) : (
                    <>
                        {subTab === "ai-insight-result" ? (
                            historyInsightData
                                ? <InsightResultsContent
                                    parsedAIResult={parseAIResult(historyInsightData.ai_email_analysis_result)}
                                    showActions={false}
                                    insightData={historyInsightData}
                                />
                                : <div style={{ padding: "16px", color: "gray", fontSize: "12px", textAlign: "center" }}>
                                    AI Insight data is not available for this scan.
                                </div>
                        ) : (
                            <DeepScanResultContent
                                showActions={false}
                                threatItems={historyDeepScanData?.threatItems}
                                indicatorStatus={historyDeepScanData?.indicatorStatus}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
